/**
 * PostEventRecapModal — Iter-220j (Sprint 1A).
 *
 * Auto-opens after talent clock-out (or on talent home if pending
 * recaps exist) and walks the talent through a star rating + feedback
 * for the shift they just finished. Decoupled from payroll approval —
 * talent leaves feedback while it's fresh, NOT after hours-approved.
 *
 * Wraps the existing `RatingFlow` component (Iter-163, talent → client
 * direction) so the multi-category rating UX stays canonical. This
 * modal adds:
 *   • A clear "Please complete your post-event recap and feedback"
 *     framing — replaces the legacy "Can only rate after gig
 *     completion and hours approval" copy.
 *   • Optional skip path with a persistent reminder badge.
 *   • Mobile-first sizing (the parent dialog already handles overflow).
 */
import React, { useState } from 'react';
import RatingFlow from './RatingFlow';
import VoiceRecapRecorder from './VoiceRecapRecorder';
import ShiftMetricsWizard from './ShiftMetricsWizard';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { ClipboardCheck, Clock3, Mic, BarChart3 } from 'lucide-react';

export const PostEventRecapModal = ({
  isOpen,
  onClose,
  booking,
  onRecapSubmitted,
}) => {
  // Iter-352 — the recap is a two-part experience:
  //   1. Rating / voice-recap  (client feedback + subjective notes)
  //   2. Shift Metrics Wizard  (numeric outputs — leads_collected,
  //      samples_distributed, qr_scans, photos, etc. THIS is where the
  //      role-conditional Lead Generation / ROI questions live.)
  // Previously the modal only offered (1), so operators reported the
  // recap "was missing ROI/lead-gen sections". After finishing the
  // rating we now surface a "Log event metrics" step that opens the
  // metrics wizard. Talent can also jump straight to it.
  const [step, setStep] = useState('intro');

  const close = () => {
    setStep('intro');
    onClose?.();
  };

  const submitted = () => {
    // After a rating or voice recap submits, immediately prompt the
    // talent to log metrics + photos. They can still close if they
    // want; the modal chain leaves the shift flagged as recap-required
    // if any part is incomplete.
    setStep('metrics');
  };

  const metricsSubmitted = () => {
    setStep('intro');
    onRecapSubmitted?.();
    onClose?.();
  };

  if (!booking) return null;

  // Metrics wizard is a bare embeddable component; wrap it in a modal
  // overlay so it appears centered on top (not inline off-screen).
  if (step === 'metrics') {
    return (
      <div
        className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center p-3 overflow-y-auto"
        data-testid="recap-metrics-wizard-overlay"
        onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      >
        <div className="bg-white rounded-xl p-4 w-full max-w-2xl my-6 max-h-[95vh] overflow-y-auto">
          <ShiftMetricsWizard
            bookingId={booking.id || booking.booking_id}
            onClose={close}
            onSubmitted={metricsSubmitted}
          />
        </div>
      </div>
    );
  }

  // When in rating step, hand off entirely to RatingFlow which renders
  // its own Dialog. We keep the wrapper Dialog closed so we don't stack
  // two modals.
  if (step === 'rating') {
    return (
      <RatingFlow
        isOpen
        onClose={close}
        booking={booking}
        ratingType="talent_to_client"
        onRatingSubmitted={submitted}
        showVoiceBanner={false}
      />
    );
  }

  if (step === 'voice') {
    return (
      <Dialog open={!!isOpen} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="sm:max-w-md" data-testid="post-event-recap-voice-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-cyan-600" />
              Voice Recap
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Hold the mic and share how the shift went. AI handles the rest.
            </DialogDescription>
          </DialogHeader>
          <VoiceRecapRecorder
            booking={booking}
            onConfirmed={submitted}
            onCancel={() => setStep('rating')}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={!!isOpen} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-md" data-testid="post-event-recap-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-cyan-600" />
            Please complete your post-event recap
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Quick feedback while it&apos;s fresh — takes under a minute.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="font-medium text-gray-900" data-testid="post-event-recap-shift-title">
              {booking.shift_title || booking.venue_name || 'Shift'}
            </div>
            {booking.client_name && (
              <div className="text-xs text-gray-600 mt-0.5">
                Client: {booking.client_name}
              </div>
            )}
            {booking.shift_end && (
              <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                <Clock3 className="w-3 h-3" />
                Ended {new Date(booking.shift_end).toLocaleString()}
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 leading-relaxed">
            Your recap has <strong>two parts</strong>:
            {' '}(1) <em>Rate your assignment &amp; client</em> — voice or typed;
            {' '}(2) <em>Log event metrics</em> — leads collected, samples
            distributed, photos, and any campaign-specific outputs.
            Both stay flagged as <strong>Post-Event Recap Required</strong>{' '}
            until finished, and may block new shift applications after 7 days.
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:gap-2">
          <Button
            type="button"
            onClick={() => setStep('voice')}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5"
            data-testid="post-event-recap-start-btn"
          >
            <Mic className="w-4 h-4" /> Record voice recap
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full gap-1.5"
            onClick={() => setStep('rating')}
            data-testid="post-event-recap-type-btn"
          >
            <ClipboardCheck className="w-4 h-4" />
            Type feedback instead
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full gap-1.5"
            onClick={() => setStep('metrics')}
            data-testid="post-event-recap-metrics-btn"
          >
            <BarChart3 className="w-4 h-4" />
            Log event metrics (leads, samples, photos)
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full text-slate-500"
            onClick={close}
            data-testid="post-event-recap-remind-later-btn"
          >
            Remind me later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostEventRecapModal;
